import { Button, Collapse, Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/ui-components/Icon';
import GrayChevronDown from '../../../assets/images/gray-chevron-down.svg';
import GreenTick from '../../../assets/images/green-tick.svg';
import RedTick from '../../../assets/images/red-tick.svg';
import WhiteRightArrow from '../../../assets/images/arrow-right.svg';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';

const { Panel } = Collapse;
const { Text } = Typography;
const { useBreakpoint } = Grid;

interface InterviewSectionProps {
	title: string;
	isCompleted?: boolean;
	description: string;
	buttonText: string;
	onButtonClick: () => void;
	skipModal?: boolean;
}

const Header = ({
	title,
	isCompleted,
}: {
	title: string;
	isCompleted?: boolean;
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-row items-center gap-4">
			<Icon src={isCompleted ? GreenTick : RedTick} width={18} height={18} />
			<Text className="font-poppins font-semibold text-xs text-gray-650 break-words max-w-[200px] leading-tight">
				{t(title)}
			</Text>
		</div>
	);
};

export const InterviewSection = ({
	title,
	description,
	isCompleted,
	buttonText,
	onButtonClick,
	skipModal,
}: InterviewSectionProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { confirmPopUpAction } = useConfirmPopup();

	const handleButtonClick = () => {
		if (isCompleted && !skipModal) {
			confirmPopUpAction(
				t('confirmationRequired'),
				t('areYouSureYouWantToProceedAndEdit'),
				onButtonClick,
				'proceed'
			);
		} else {
			onButtonClick();
		}
	};

	const icon = (
		<Icon src={isCompleted ? GreenTick : RedTick} width={18} height={18} />
	);

	return (
		<>
			{isMobile ? (
				<Collapse
					collapsible="header"
					ghost
					expandIconPosition="end"
					expandIcon={({ isActive }) => (
						<span
							className={`transition-transform ${
								isActive ? 'rotate-180' : 'rotate-0'
							}`}
						>
							<Icon src={GrayChevronDown} width={18} height={18} />
						</span>
					)}
					className="border border-solid border-gray-200"
				>
					<Panel
						header={<Header title={title} isCompleted={isCompleted} />}
						key="1"
					>
						<div className="flex flex-col">
							<Text className="font-poppins font-medium text-secondary-gray text-sm">
								{t(description)}
							</Text>
							<div className="flex mt-[8px] justify-center">
								<Button
									type="primary"
									block={false}
									onClick={handleButtonClick}
									icon={<Icon src={WhiteRightArrow} width={18} height={18} />}
									iconPosition="end"
									className="max-w-xs sm:max-w-sm font-bold flex items-center leading-none break-words text-center"
								>
									<span className="text-xs">{t(buttonText)}</span>
								</Button>
							</div>
						</div>
					</Panel>
				</Collapse>
			) : (
				<div className="flex w-[736px] gap-20 justify-between items-center rounded-md py-4 px-8 border border-solid border-gray-200">
					<div className="flex gap-8">
						<div className="self-start">{icon}</div>
						<div className="flex flex-col gap-1">
							<Text className="font-bold text-sm text-gray-650">
								{t(title)}
							</Text>
							<Text className="font-normal text-secondary-gray text-sm">
								{t(description)}
							</Text>
						</div>
					</div>
					<Text
						className="font-bold text-primary-blue text-sm whitespace-nowrap cursor-pointer"
						onClick={handleButtonClick}
					>
						{t(buttonText)}
					</Text>
				</div>
			)}
		</>
	);
};
